import {
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { XlsxImportError, XlsxImportResponse } from '@gozoki/api-types';
import { _assert } from '@gozoki/tools';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ThemeColors } from '../utils/theme/colors.theme';
import { fonts } from '../utils/theme/fonts.theme';
import { ButtonOverrides } from '../utils/theme/overrides.theme';
import Center from './containers/Center';
import FileInputRhf from './inputs/FileInputRhf';
import Page from './Page';

type ResourceName = string | { singular: string; plural: string };

interface XlsxImportProps {
    goBackRoute: string;
    resourceName: ResourceName;
    onSubmit: (file: File) => Promise<XlsxImportFeedbacks>;
}

export const XslxImport = ({ onSubmit, goBackRoute, resourceName }: XlsxImportProps) => {
    const navigate = useNavigate();

    const { control, getValues, watch } = useForm<{ file: File }>();

    const [uploadFeedbacks, setUploadFeedbacks] = useState<XlsxImportFeedbacks>();

    const [isUpdatingProductMinStock, setIsUpdatingProductMinStock] = useState(false);

    const singularResourceName =
        typeof resourceName === 'string' ? resourceName : resourceName.singular;
    const pluralResourceName =
        typeof resourceName === 'string' ? `${resourceName}s` : resourceName.plural;

    const cancel = () => {
        navigate(goBackRoute);
    };

    const handleUploadMinimumStock = async () => {
        const file = getValues('file');

        _assert(file);

        setIsUpdatingProductMinStock(true);
        const results = await onSubmit(file);
        setIsUpdatingProductMinStock(false);

        setUploadFeedbacks(results);
    };

    return (
        <Page sx={{ maxWidth: '50vw', margin: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={cancel}>
                    <ArrowBackIcon
                        sx={{
                            color: ThemeColors.BLACK,
                            height: '24px',
                            width: '24px',
                        }}
                    />
                </IconButton>
                <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
                    Importer un fichier Excel
                </Typography>

                <Button
                    variant="outlined"
                    sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
                    onClick={cancel}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleUploadMinimumStock}
                    disabled={isUpdatingProductMinStock || watch('file') === undefined}
                    style={{ marginLeft: '16px' }}
                >
                    Importer les {pluralResourceName}
                </Button>
            </div>
            <Paper sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}>
                <Typography mb="8px" style={fonts.inputHeader}>
                    Fichier
                </Typography>
                <FileInputRhf control={control} name="file" label="Fichier Excel" accept=".xlsx" />
                {isUpdatingProductMinStock && (
                    <Center style={{ marginTop: '32px' }}>
                        <CircularProgress />
                    </Center>
                )}
                {uploadFeedbacks && (
                    <XlsxImportFeedbacks
                        resourceName={{
                            plural: pluralResourceName,
                            singular: singularResourceName,
                        }}
                        feedbacks={uploadFeedbacks}
                    />
                )}
            </Paper>
        </Page>
    );
};

type XlsxImportFeedbacks =
    | {
          data: XlsxImportResponse;
          error?: null;
      }
    | {
          data?: null;
          error: XlsxImportError;
      };

type XlsxImportFeedbacksProps = {
    resourceName: Exclude<ResourceName, string>;
    feedbacks: XlsxImportFeedbacks;
};

export const XlsxImportFeedbacks = ({ resourceName, feedbacks }: XlsxImportFeedbacksProps) => {
    return (
        <>
            <Divider sx={{ marginTop: '36px', marginBottom: '16px' }} />
            <Typography style={fonts.inputHeader} mb="8px">
                Importation des {resourceName.plural}
            </Typography>
            {feedbacks.data && (
                <>
                    <Typography style={fonts.userTag}>
                        {updateProductMessage(feedbacks.data.updated)}
                    </Typography>
                    <Typography style={fonts.userTag}>
                        {createProductMessage(feedbacks.data.created)}
                    </Typography>
                    <Typography style={fonts.userTag}>
                        {insertProductErrorMessage(feedbacks.data.failed)}
                    </Typography>
                    {feedbacks.data.errors.length > 0 && (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={fonts.inputHeader}>Référence</TableCell>
                                    <TableCell align="left" style={fonts.inputHeader}>
                                        Erreur
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {feedbacks.data.errors.map((item, index) => (
                                    <TableRow key={`${index}-${item.reference}}`}>
                                        <TableCell style={fonts.userTag}>
                                            {item.reference}
                                        </TableCell>
                                        <TableCell>
                                            <span>{item.message}</span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </>
            )}
            {feedbacks.error && (
                <>
                    <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />
                    <Typography style={fonts.inputHeader} mb="8px">
                        Erreurs
                    </Typography>
                    <Typography style={fonts.userTag}>{feedbacks.error.message}</Typography>
                    <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />
                    <Typography style={fonts.inputHeader} mb="8px">
                        Détail des erreurs
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={fonts.inputHeader}>Ligne</TableCell>
                                <TableCell align="left" style={fonts.inputHeader}>
                                    Erreur
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feedbacks.error.errors.map((item, index) => (
                                <TableRow key={`${index}-${item.row}}`}>
                                    <TableCell style={fonts.userTag}>{item.row}</TableCell>
                                    <TableCell>
                                        <span>{item.message}</span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </>
    );
};

// Helper methods for singular / plural handling
const updateProductMessage = (count: number) => {
    if (count === 1) return `1 a été mis à jour`;
    return `${count} ont été mis à jour`;
};

const createProductMessage = (count: number) => {
    if (count === 1) return `1 a été créé`;
    return `${count} ont été créés`;
};

const insertProductErrorMessage = (count: number) => {
    if (count === 1) {
        return `1 n'a pas pu être inséré dans la base de données`;
    }
    return `${count} n'ont pas pu être insérés dans la base de données`;
};
